/**
 * Per-call Khmer STT model options for the CallCenter UI.
 *
 * Values are sent as the WS `stt_model` query param:
 *   - "v1"           → local Khmer model 1
 *   - "v5"           → parakeet-khmer-v5 (model 2)
 *   - "v4" (default) → parakeet-khmer-v4 (model 3)
 *
 * All services run in parallel on the backend; this only picks which
 * checkpoint the call routes to. Adding a model = one entry below (plus a
 * compose service + config.py URL on the backend).
 */

export interface SttModelOption {
  value: string;
  label: string;
  description?: string;
}

export const STT_MODEL_OPTIONS: SttModelOption[] = [
  {
    value: "v1",
    label: "Model 1",
    description: "Khmer local model 1",
  },
  {
    value: "v5",
    label: "Model 2 — v5",
    description: "parakeet-khmer-v5",
  },
  {
    value: "v4",
    label: "Model 3 — v4",
    description: "parakeet-khmer-v4 (default)",
  },
];

export const DEFAULT_STT_MODEL = "v4";

const VALID_STT_MODELS = new Set(STT_MODEL_OPTIONS.map((o) => o.value));

/** Canonicalize any stored/incoming value; unknown → default. */
export function normalizeSttModel(value: string | undefined | null): string {
  const v = (value || "").trim().toLowerCase();
  return VALID_STT_MODELS.has(v) ? v : DEFAULT_STT_MODEL;
}

export const STT_MODEL_SELECT_OPTIONS = STT_MODEL_OPTIONS.map((o) => ({
  value: o.value,
  label: o.description ? `${o.label} (${o.description})` : o.label,
}));

/** Short badge text for the floating settings chip. */
export function sttModelBadge(value: string | undefined | null): string {
  return normalizeSttModel(value);
}

/** localStorage key (migrates from legacy khmer_stt_model). */
export const STT_MODEL_STORAGE_KEY = "stt_model";
export const STT_MODEL_STORAGE_KEY_LEGACY = "khmer_stt_model";

export function readStoredSttModel(): string {
  try {
    // Anything not in STT_MODEL_OPTIONS (e.g. leftover cloud provider values
    // from a previous build) falls back to the default.
    return normalizeSttModel(
      localStorage.getItem(STT_MODEL_STORAGE_KEY) ||
        localStorage.getItem(STT_MODEL_STORAGE_KEY_LEGACY)
    );
  } catch {
    return DEFAULT_STT_MODEL;
  }
}

export function writeStoredSttModel(value: string): void {
  try {
    localStorage.setItem(STT_MODEL_STORAGE_KEY, normalizeSttModel(value));
  } catch {
    /* ignore */
  }
}
